import React from 'react';
import { ProgramLink, ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const DivisionBiblicalStudies = () => {
  const programs = [
    {
      title: "Master of Arts in Biblical Studies",
      path: "/academics/ma_biblical_studies",
      description: "Advanced study in biblical interpretation and theological research",
      icon: "📚"
    },
    {
      title: "Master of Arts in Old Testament Studies",
      path: "/academics/ma_ot_studies",
      description: "Specialized focus on Old Testament texts and ancient Near Eastern context",
      icon: "📜"
    },
    {
      title: "Master of Arts in New Testament Studies",
      path: "/academics/ma_nt_studies",
      description: "In-depth study of New Testament literature and early Christian history",
      icon: "✝️"
    }
  ];

  const features = [
    {
      title: "Biblical Languages",
      description: "Comprehensive training in Hebrew and Greek for biblical research",
      icon: "🔤"
    },
    {
      title: "Theological Research",
      description: "Advanced methodologies for theological and biblical scholarship",
      icon: "🔍"
    },
    {
      title: "Historical Context",
      description: "Deep understanding of ancient Near Eastern and Mediterranean cultures",
      icon: "🏺"
    },
    {
      title: "Practical Application",
      description: "Integration of academic study with contemporary ministry",
      icon: "🤝"
    }
  ];

  const resources = [
    {
      title: "Biblical Research Center",
      description: "Access to extensive biblical and theological research resources",
      path: "/academics/biblical-studies/research-center"
    },
    {
      title: "Language Labs",
      description: "Advanced facilities for biblical language study",
      path: "/academics/biblical-studies/language-labs"
    },
    {
      title: "Archaeological Studies",
      description: "Opportunities for archaeological research and field work",
      path: "/academics/biblical-studies/archaeology"
    }
  ];

  const facultyHighlights = [
    {
      title: "Biblical Scholarship",
      description: "Internationally recognized scholars in biblical studies",
      icon: "👨‍🏫"
    },
    {
      title: "Publication Record",
      description: "Faculty actively engaged in academic publication and research",
      icon: "📖"
    },
    {
      title: "Ministry Experience",
      description: "Faculty with extensive practical ministry background",
      icon: "⛪"
    },
    {
      title: "Mentorship",
      description: "Personal guidance from experienced scholars and practitioners",
      icon: "🤝"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Division of Biblical Studies and Theology
            </h1>
            <p className="text-xl opacity-90">
              Advanced scholarship in biblical interpretation and theological research
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Division Overview</h2>
              <p className="text-gray-600 mb-4">
                The Division of Biblical Studies and Theology offers advanced programs designed 
                to prepare scholars and ministers for in-depth biblical research and theological 
                reflection. Learn more about our{' '}
                <InlineLink to="/academics/biblical-studies/mission">academic mission</InlineLink> and{' '}
                <InlineLink to="/academics/biblical-studies/approach">research methodology</InlineLink>.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Available Programs */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Available Programs</h2>
            <div className="space-y-4">
              {programs.map((program, index) => (
                <ProgramLink 
                  key={index}
                  title={program.title}
                  description={program.description}
                  path={program.path}
                  icon={program.icon}
                />
              ))}
            </div>
          </section>

          {/* Faculty Highlights */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Faculty Excellence</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {facultyHighlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{highlight.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600">{highlight.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Research Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to biblical research and theological education.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DivisionBiblicalStudies; 